/** Prototype design catalog — dummy enrichments when ConverseEngine returns thin cards. */
import type { Project } from './types';
import type { MarketIntel } from './advisor-api';

export interface PrototypeProjectDetail extends Project {
  /** Illustrative-only fields when no live NayaDesk payload. */
  dummy?: boolean;
  live?: boolean;
  summary?: string;
  projectType?: string;
  configurations?: Array<{
    unitType: string;
    priceDisplay: string;
    priceMinInr: number;
    sizeDisplay?: string;
  }>;
  locationIntel?: {
    connectivity_summary?: string;
    micro_market_overview?: string;
    nearby_pois?: string[];
    drive_times?: string[];
  };
  faqs?: Array<{ question_key: string; question: string; answer: string }>;
  /** Live, provenance-carrying corridor intel — never fabricated. Only set
   *  from a NayaDesk payload; the dummy overlay must never populate it. */
  marketIntel?: MarketIntel;
  psf: number;
  mktPsf: number;
  cagr: number;
  mktCagr: number;
  yieldPct: number;
  history: number[];
  rera: string;
  insight: string;
  banks: string;
  cfg: {
    label: string;
    carpet: number;
    superb: number;
    facing: string;
    ceiling: string;
    parkIncluded: boolean;
  };
  statuses: Array<{ label: string; value: string }>;
  timeline: Array<{ date: string; title: string; detail: string }>;
}

const PROTOTYPE: Record<string, PrototypeProjectDetail> = {
  ayana: {
    id: 'ayana',
    name: 'Ayana',
    builder: 'Lokations',
    location: 'Whitefield · Varthur belt',
    price: '₹58–62L',
    bhk: '2 BHK',
    sqft: '892 sqft',
    possession: 'Dec 2027',
    commute: '~25 min to ORR',
    tag: 'Best fit',
    fit: 'Lands inside your budget with the most efficient plate of the three.',
    fitTone: 'success',
    accent: '#9a7734',
    dummy: true,
    psf: 5480,
    mktPsf: 5120,
    cagr: 8.2,
    mktCagr: 6.4,
    yieldPct: 3.1,
    history: [100, 108, 115, 124, 134],
    rera: 'PRM/KA/RERA/1251/446/PR/171120/001234',
    insight: 'Outpaced its micro-market by ~1.8pp CAGR — ORR connectivity upgrades and IT-corridor demand.',
    banks: 'SBI, HDFC and ICICI',
    cfg: {
      label: '2 BHK · Type B (East)',
      carpet: 892,
      superb: 1085,
      facing: 'East',
      ceiling: '9.6 ft',
      parkIncluded: false,
    },
    statuses: [
      { label: 'RERA', value: 'Registered · active' },
      { label: 'NA conversion', value: 'Residential · approved' },
      { label: 'EC / Khata', value: 'EC available · A-Khata path' },
      { label: 'Title', value: 'Clear · no litigation' },
      { label: 'Loan eligibility', value: 'SBI · HDFC · ICICI' },
    ],
    timeline: [
      { date: 'Nov 2017', title: 'RERA registration', detail: 'Phase 1 registered under Karnataka RERA.' },
      { date: 'Mar 2019', title: 'NA conversion', detail: 'Land use converted to residential township zone.' },
      { date: 'Jun 2024', title: 'Phase 1 OC track', detail: 'Structural completion on schedule for Dec 2027 possession.' },
    ],
  },
  buena: {
    id: 'buena',
    name: 'Buena Vista',
    builder: 'Brigade Group',
    location: 'Whitefield · premium',
    price: '₹72–78L',
    bhk: '2 BHK',
    sqft: '968 sqft',
    possession: 'Jun 2027',
    commute: '~25 min to ORR',
    tag: 'Location',
    fit: 'A stretch on budget, but the strongest micro-market and earliest possession.',
    fitTone: 'stretch',
    accent: '#9a7734',
    dummy: true,
    psf: 6280,
    mktPsf: 5120,
    cagr: 7.4,
    mktCagr: 6.4,
    yieldPct: 2.8,
    history: [100, 106, 112, 119, 127],
    rera: 'PRM/KA/RERA/1251/308/PR/190615/002891',
    insight: 'Premium to market on ₹/sqft — justified by builder brand and a walk-to-metro pipeline (2027).',
    banks: 'all major banks',
    cfg: {
      label: '2 BHK · Type A (North-East)',
      carpet: 968,
      superb: 1210,
      facing: 'North-East',
      ceiling: '10 ft',
      parkIncluded: true,
    },
    statuses: [
      { label: 'RERA', value: 'Registered · Phase 2' },
      { label: 'NA conversion', value: 'Approved' },
      { label: 'EC / Khata', value: 'EC on request' },
      { label: 'Title', value: 'Clear' },
      { label: 'Loan eligibility', value: 'All major banks' },
    ],
    timeline: [
      { date: 'Jun 2019', title: 'RERA registration', detail: 'Buena Vista Phase 2 registered.' },
      { date: '2021', title: 'Launch tranche', detail: 'Tower B launch; 68% sold in 18 months (builder disclosed).' },
      { date: '2024', title: 'Construction milestone', detail: 'Floor 12 cast; on track for Jun 2027.' },
    ],
  },
  krishnaja: {
    id: 'krishnaja',
    name: 'Krishnaja Greens',
    builder: 'Lokations',
    location: 'Sarjapur · ORR south',
    price: '₹46–52L',
    bhk: '2 BHK',
    sqft: '812 sqft',
    possession: 'Mar 2028',
    commute: '~40 min to ORR',
    tag: 'Best value',
    fit: 'Best entry price and highest growth — the trade-off is a longer commute.',
    fitTone: 'success',
    accent: '#9a7734',
    dummy: true,
    psf: 4680,
    mktPsf: 4210,
    cagr: 9.1,
    mktCagr: 7.2,
    yieldPct: 3.4,
    history: [100, 110, 118, 128, 139],
    rera: 'PRM/KA/RERA/1251/446/PR/200821/003102',
    insight: 'Highest CAGR in your shortlist — entry price still below Whitefield; commute is the main factor.',
    banks: 'HDFC, Axis and SBI',
    cfg: {
      label: '2 BHK · Type C (Garden view)',
      carpet: 812,
      superb: 1012,
      facing: 'West',
      ceiling: '9.5 ft',
      parkIncluded: false,
    },
    statuses: [
      { label: 'RERA', value: 'Registered' },
      { label: 'NA conversion', value: 'Approved · township' },
      { label: 'EC / Khata', value: 'EC available' },
      { label: 'Title', value: 'Clear' },
      { label: 'Loan eligibility', value: 'HDFC · Axis · SBI' },
    ],
    timeline: [
      { date: 'Aug 2020', title: 'RERA registration', detail: 'Registered as plotted + apartment mix.' },
      { date: '2022', title: 'Infrastructure phase', detail: 'Internal roads and STP commissioned.' },
      { date: '2025', title: 'Tower handover track', detail: 'Possession Mar 2028 per RERA quarterly filing.' },
    ],
  },
};

export const PROTOTYPE_FLOORS = [4, 8, 12, 15] as const;

export const TOOL_INDEX = [
  { n: '01', label: 'Matches for your brief' },
  { n: '02', label: 'Floor plans & unit picker' },
  { n: '03', label: 'Legal & title pack' },
  { n: '04', label: 'Price trends & ₹/sqft' },
  { n: '05', label: 'Home-loan & EMI fit' },
  { n: '06', label: 'Saturday visit route' },
];

export const VISIT_DATES = [
  { id: 'd1', label: 'Sat, 11 Jul', sub: 'this week' },
  { id: 'd2', label: 'Sat, 18 Jul', sub: 'next week' },
  { id: 'd3', label: 'Sat, 25 Jul', sub: 'in 3 weeks' },
];

export const CONCIERGE_SERVICES = [
  {
    id: 'advisor',
    tone: 'base' as const,
    title: 'Advisor',
    price: 'Free',
    desc: 'Every tool, document and price on this board — floor plans, the legal pack, trends and EMI. Completely anonymous.',
    points: ['Unlimited, private research', 'No number ever shared', 'You decide, always'],
    cta: "You're on this now",
  },
  {
    id: 'visit',
    tone: 'featured' as const,
    title: 'Visit day',
    price: '₹3,500',
    desc: 'We plan the optimised route, coordinate slots and ride along for the day.',
    points: ['Optimised Saturday route', 'Slots booked for you', 'Number shared with that day’s builders only'],
    cta: 'Plan a visit day',
  },
  {
    id: 'legal',
    tone: 'plain' as const,
    title: 'Legal & loan',
    price: 'from ₹15,000',
    desc: 'Agreement review, title diligence and home-loan processing with listed banks.',
    points: ['Sale-agreement review', 'Title & EC diligence', 'Loan paperwork with SBI / HDFC / ICICI'],
    cta: 'Talk to Naya',
  },
  {
    id: 'negotiation',
    tone: 'plain' as const,
    title: 'Negotiation',
    price: 'from ₹35,000',
    desc: 'Floor-rise, flexi-pay and counter-offers handled on your behalf — you never sit across the table.',
    points: ['Price & payment-plan negotiation', 'Handled anonymously until you sign'],
    cta: 'Talk to Naya',
  },
  {
    id: 'full',
    tone: 'plain' as const,
    title: 'Full concierge',
    price: 'from ₹75,000',
    desc: 'Search to registration — advisory, legal, loan and negotiation, end to end.',
    points: ['Everything above, one contact', 'Right up to registration'],
    cta: 'Talk to Naya',
  },
];

const DRIVE: Record<string, number> = {
  'ayana|buena': 12,
  'ayana|krishnaja': 35,
  'buena|krishnaja': 40,
};

function normalizeId(id: string): string {
  return id.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function findPrototype(project: Project): PrototypeProjectDetail | null {
  const byId = PROTOTYPE[normalizeId(project.id)];
  if (byId) return byId;
  const byName = Object.values(PROTOTYPE).find(
    (p) => p.name.toLowerCase() === project.name.toLowerCase(),
  );
  return byName ?? null;
}

/** Merge live bot card with prototype tool payloads (tagged dummy). */
export function enrichProject(project: Project): PrototypeProjectDetail {
  const proto = findPrototype(project);
  if (proto) {
    return {
      ...proto,
      ...project,
      id: project.id,
      name: project.name,
      price: project.price || proto.price,
      location: project.location || proto.location,
      bhk: project.bhk || proto.bhk,
      dummy: true,
    };
  }
  return {
    ...project,
    dummy: true,
    psf: 5200,
    mktPsf: 4800,
    cagr: 7.5,
    mktCagr: 6.5,
    yieldPct: 3.0,
    history: [100, 105, 110, 116, 122],
    rera: 'dummy · pending from NayaDesk',
    insight: 'Dummy trend copy — live legal/trends coming from engine.',
    banks: 'SBI · HDFC · ICICI',
    cfg: {
      label: project.bhk || '2 BHK',
      carpet: 900,
      superb: 1100,
      facing: 'East',
      ceiling: '9.5 ft',
      parkIncluded: false,
    },
    statuses: [{ label: 'RERA', value: 'dummy · check with Naya' }],
    timeline: [{ date: '—', title: 'Dummy timeline', detail: 'Replace when project detail API lands.' }],
    tag: project.tag ?? 'Match',
    fit: project.fit || 'From your brief — details are illustrative until NayaDesk syncs.',
    fitTone: project.fitTone ?? 'neutral',
    sqft: project.sqft ?? '— sqft',
    possession: project.possession ?? 'TBD',
    commute: project.commute ?? '',
    accent: project.accent ?? '#9a7734',
  };
}

export function enrichProjects(projects: Project[]): PrototypeProjectDetail[] {
  return projects.map(enrichProject);
}

export type UnitStatus = 'available' | 'sold' | 'hold';

export interface PlateUnit {
  no: string;
  id: string;
  status: UnitStatus;
  base: number;
  rise: number;
  car: number;
  view: number;
  total: number;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function ustatus(seed: string): UnitStatus {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const r = Math.abs(h) % 10;
  if (r === 0) return 'hold';
  if (r <= 3) return 'sold';
  return 'available';
}

export function buildPlate(project: PrototypeProjectDetail, floor: number): PlateUnit[] {
  const base = parseFloat(project.price.replace(/[^\d.]/g, '')) || 58;
  const cfg = project.cfg;
  const rise = Math.max(0, floor - 4) * 0.35;
  const units: PlateUnit[] = [];
  let n = 0;
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < 4; c++) {
      n++;
      const no = `${floor}${String(n).padStart(2, '0')}`;
      const id = `${project.id}-${no}`;
      const status = ustatus(id);
      const baseL = base + (cfg.carpet - 900) * 0.008;
      const car = cfg.parkIncluded ? 0 : 2.5;
      const view = floor >= 15 ? 1.2 : floor >= 8 ? 0.6 : 0;
      const total = round1(baseL + rise + car + view);
      units.push({
        no,
        id,
        status,
        base: round1(baseL),
        rise: round1(rise),
        car,
        view,
        total,
      });
    }
  }
  return units;
}

export function fmtL(n: number): string {
  return `₹${n.toFixed(1)}L`;
}

export function fmtInr(rupees: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Math.round(rupees));
}

export function emiCalc(
  totalL: number,
  downPct: number,
  ratePct: number,
  tenureYears: number,
) {
  const P = totalL * 1e5 * (1 - downPct / 100);
  const mr = ratePct / 1200;
  const nn = tenureYears * 12;
  const emi =
    mr === 0 ? P / nn : (P * mr * Math.pow(1 + mr, nn)) / (Math.pow(1 + mr, nn) - 1);
  return {
    P,
    emi,
    interest: emi * nn - P,
    downAmt: totalL * 1e5 * (downPct / 100),
  };
}

export function emiBaseL(project: PrototypeProjectDetail): number {
  const base = parseFloat(project.price.replace(/[^\d.]/g, '')) || 58;
  return round1(base + (project.cfg.parkIncluded ? 0 : 2.5));
}

export function driveMinutes(a: string, b: string): number {
  const key = [a, b].sort().join('|');
  return DRIVE[key] ?? 30;
}

export function fmtClock(mins: number): string {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')} ${ap}`;
}
